<template>
	<v-container class="space-y-lg pb-xl md:space-y-2xl">
		<div class="mx-sm space-y-md sm:mx-0">
			<v-h3 tag="h1">
				{{ story.name }}
			</v-h3>

			<v-storyblok-component
				:is="story.content.active_campaigns[0]"
				v-if="hasActiveCampaigns"
				:story="story.content.active_campaigns"
				:blok="story.content.active_campaigns[0]"
				:options="{isEndDateHidden: true}"
			/>

			<sb-rich-text
				v-if="story.content.description"
				:document="story.content.description"
				class="prose-5xs text-(--font-color)"
				:style="{
					'--font-color': story.content.description_color?.color,
				}"
			/>
		</div>

		<!-- Sub categories menu-->
		<div
			v-if="categoryItems.length > 0"
			class="grid grid-cols-1 gap-xs px-sm sm:px-0 md:grid-cols-3 md:gap-sm lg:grid-cols-4"
		>
			<nuxt-link
				v-for="(item, index) in categoryItems"
				:key="item.key"
				:to="item.url"
				:aria-label="item.ariaLabel"
				:class="{
					// Elements that should be initially hidden in mobile but visible on desktop
					'hidden md:flex':
						!isCategoryCollapseOpen &&
						index >= visibleCategoriesCount.mobile &&
						index < visibleCategoriesCount.desktop,
					// Elements that should be hidden on desktop too if collapse isn't open
					hidden:
						!isCategoryCollapseOpen && index >= visibleCategoriesCount.desktop,
				}"
				class="group flex h-15 items-center overflow-hidden rounded bg-brand-sand hover:bg-brand-sand/50"
			>
				<!-- Image defined just for menu. These are displayed at a
                height that leaves a bit of padding above and below the image
                -->
				<!-- Preloading so that the browser downloads the image even if it's hidden behind the
                "show more" button -->
				<nuxt-picture
					v-if="item.imageSrc"
					:src="item.imageSrc"
					provider="storyblok"
					loading="lazy"
					:preload="{fetchPriority: 'low'}"
					height="50"
					fit="in"
					class="w-[70px] shrink-0 px-xs sm:w-20"
					:alt="item.alt"
					:aria-label="t('aria.navigate', {page: item.name})"
					:img-attrs="{
						class: 'mx-auto h-[50px] object-contain mix-blend-multiply',
					}"
				/>

				<!-- If no dedicated image for the category is defined, then
                the fallback should be a "mood picture" which should fill the
                entire vertical space -->
				<nuxt-picture
					v-else-if="item.fallbackImageSrc"
					:src="item.fallbackImageSrc"
					provider="storyblok"
					loading="lazy"
					:preload="{fetchPriority: 'low'}"
					height="60"
					fit="in"
					class="w-[70px] shrink-0 sm:w-20"
					:alt="item.alt"
					:aria-label="t('aria.navigate', {page: item.name})"
					:img-attrs="{
						class: 'h-[60px] w-full object-cover mix-blend-multiply',
					}"
				/>

				<!-- Text content of menu. -->
				<p
					class="col-span-3 flex h-full items-center px-sm font-prenton-cond text-base leading-tight group-hover:underline xl:text-lg"
				>
					<span class="line-clamp-2">
						{{ item.title }}
					</span>
				</p>
			</nuxt-link>

			<v-button
				v-if="hasHiddenCategoriesInMobile || hasHiddenCategoriesInDesktop"
				type="button"
				theme="black"
				size="sm"
				class="flex h-15 w-full items-center justify-center space-x-xs hover:underline"
				:class="{
					'md:hidden': !hasHiddenCategoriesInDesktop,
				}"
				@click="isCategoryCollapseOpen = !isCategoryCollapseOpen"
			>
				<span v-if="!isCategoryCollapseOpen">{{
					t("show-all-categories")
				}}</span>

				<span v-else>{{ t("show-less-categories") }}</span>

				<v-icon
					:icon="isCategoryCollapseOpen ? 'chevron-up' : 'chevron-down'"
					class="text-lg"
				></v-icon>
			</v-button>
		</div>

		<v-elevate-product-list
			v-slot="{
				products,
				productCount: listProductCount,
				hasNextPage,
				loadMore,
				getAdForIndex,
				showCtaForIndex,
				activeFiltersCount,
				isLoading,
				clearFilter,
			}"
			:campaign-only="isCampaignOnly"
			load-ads
			:category-id="story.content.products.source?.id"
			:query="searchQuery"
			class="space-y-lg px-sm sm:px-0"
		>
			<div class="flex flex-col justify-between">
				<p class="text-sm tracking-xl text-gray-500 md:block">
					{{ listProductCount }} {{ t("amount-products") }}
				</p>

				<div class="mt-sm flex gap-lg lg:pt-md">
					<v-button
						type="button"
						theme="black"
						size="sm"
						class="flex items-center space-x-xs hover:underline"
						@click="isModalOpen = true"
					>
						<template v-if="activeFiltersCount > 0">
							<v-category-filter-count
								:active-filters-count="activeFiltersCount"
							/>
						</template>

						<span>{{ t("filter-title") }}</span>

						<v-icon icon="filter" class="text-lg"></v-icon>
					</v-button>

					<v-button
						v-if="activeFiltersCount > 0"
						theme="gray"
						size="sm"
						@click="clearFilter"
					>
						{{ t("filter-clear") }}
					</v-button>
				</div>

				<v-elevate-common-facets class="mt-xs" hide-on-mobile />

				<div class="mt-md flex items-center justify-between gap-sm">
					<v-category-order />

					<v-category-preferred-image-type-toggle />
				</div>

				<v-category-search
					:is-loading="isLoading"
					class="mt-md max-w-[450px]"
				/>
			</div>

			<div
				class="grid grid-cols-2 gap-x-xs gap-y-lg md:grid-cols-3 md:gap-x-sm md:gap-y-xl lg:grid-cols-4"
			>
				<template v-if="isLoading && products.length === 0">
					<v-category-product-skeleton v-for="index in 8" :key="index" />
				</template>

				<template v-for="(product, index) in products" :key="product.key">
					<v-category-cta
						v-if="ctaContent != null && isCtaVisible && showCtaForIndex(index)"
						:content="ctaContent"
						class="col-span-full"
					/>

					<!-- Ads -->
					<v-storyblok-listing-ad
						v-if="getAdForIndex(index)"
						:ad="getAdForIndex(index)"
					/>

					<v-flowbox
						v-if="story.content.flowbox_key && index === 8"
						:flowbox-key="story.content.flowbox_key"
						:tags="flowboxTags"
						class="col-span-full"
					/>

					<v-elevate-listing-product-card v-if="index < 8" :product="product" />

					<LazyVisibleVElevateListingProductCard
						v-else
						:product="product"
						:hydrate-on-visible="{rootMargin: '800px'}"
					/>
				</template>
			</div>

			<div class="grid md:grid-cols-8">
				<div class="space-y-md md:col-span-2 md:col-start-4">
					<client-only>
						<v-button
							class="w-full"
							:theme="hasNextPage ? 'black' : 'gray'"
							:disabled="!hasNextPage"
							:aria-label="
								hasNextPage
									? $t('loader.show-more')
									: $t('loader.show-more-loaded')
							"
							@click="loadMore"
						>
							{{
								hasNextPage
									? $t("loader.show-more")
									: $t("loader.show-more-loaded")
							}}
						</v-button>
					</client-only>

					<v-progress-bar :total="listProductCount" :current="products.length">
						{{ $t("loader.show-more-prefix") }} {{ products.length }} /
						{{ listProductCount }} {{ $t("loader.show-more-suffix") }}
					</v-progress-bar>

					<button
						class="w-full text-base underline"
						type="button"
						@click="scrollToTop"
					>
						<span class="">{{ $t("loader.back-to-start") }}</span>
					</button>
				</div>
			</div>

			<v-category-filter-modal
				:is-open="isModalOpen"
				:results="listProductCount"
				@close="isModalOpen = false"
			/>
		</v-elevate-product-list>

		<nuxt-picture
			v-if="story.content.image?.filename"
			:src="story.content.image.filename"
			:alt="story.content.image?.alt ?? story.content.title"
			provider="storyblok"
			loading="lazy"
			sizes="xs:100vw sm:640px md:768px lg:325px xl:410px 2xl:496px"
			class="block lg:hidden"
			:img-attrs="{
				class: 'w-full rounded object-cover aspect-[21/9] lg:aspect-square',
			}"
		/>

		<div
			v-if="story.content.content"
			class="space-y-sm wrap-break-word text-gray-500"
		>
			<div
				:class="{
					'line-clamp-3 max-h-28 md:line-clamp-6 md:max-h-40 lg:max-h-none':
						!showAll,
				}"
			>
				<sb-rich-text :document="story.content.content" class="prose-5xs" />
			</div>

			<button
				v-if="!showAll"
				type="button"
				class="ring-focus text-sm outline-hidden hover:underline focus:underline lg:text-base lg:font-bold"
				@click="showAll = true"
			>
				{{ t("read-more") }}
			</button>

			<button
				v-if="showAll"
				type="button"
				class="ring-focus text-sm outline-hidden hover:underline focus:underline lg:text-base lg:font-bold"
				@click="showAll = false"
			>
				{{ t("show-less") }}
			</button>
		</div>
	</v-container>
</template>

<script setup lang="ts">
import type {ISbStoryData} from "storyblok-js-client";
import type {SbRichTextDoc as ISbRichtext} from "@storyblok/js";
import {useDefaultDynamicHead} from "@pkg/shared/composables/use-default-dynamic-head";
import {useElevateProductListState} from "@pkg/shared/composables/use-elevate-product-list-state";
import {useHyphenation} from "@pkg/shared/composables/use-hyphenation";
import type {StoryblokLink} from "@pkg/shared/composables/use-storyblok-links";
import {useStoryblokLinks} from "@pkg/shared/composables/use-storyblok-links";
import {useStoryblokStoriesList} from "@pkg/shared/composables/use-storyblok-stories-list";
import {useStoryblokStory} from "@pkg/shared/composables/use-storyblok-story";
import formatSbUrl from "@pkg/shared/utils/format-sb-url";
import type {
	BlokAsset,
	BlokLink,
	MetaFieldsPlugin,
	CmdProductListPlugin,
	BlokColorpicker,
	ListingAdComponent,
} from "@pkg/shared/types";

/**
 *  There was a super weird nuxt hydration bug where `hasNextPage` would
    evaluate to true in some attributes passed to the button and to
    false in some other attributes. Ie the button would show the "load more"
    text but still be disabled and greyed out even though there should be
    more matches to load. This would happen randomly when navigating to
    the page from any external (non-nuxt) link to this page. Also, when
    this occurred the initial SSR render seemed to return an empty product
    list on initial page load (for reasons unknown)
 */

const properties = defineProps<{
	story: ISbStoryData<CategoryLander>;
}>();

defineEmits<{
	(event: "clear-gp-filter"): void;
}>();

interface CategoryLander {
	component: "sb-category-lander";
	meta: MetaFieldsPlugin;
	image: BlokAsset;
	title: string;
	category_menu: string[];

	brand_links: BrandLink[];
	content: ISbRichtext;
	products: CmdProductListPlugin;
	campaign_only: boolean;
	description: ISbRichtext;
	short_description: string;
	description_color: BlokColorpicker;
	on_pages: string[];
	ads: ListingAdComponent[];
	active_campaigns: ISbStoryData[];
	category_text_color: BlokColorpicker;
	category_background_color: BlokColorpicker;
	flowbox_key: string | undefined;
	flowbox_tags: string | undefined;
}

interface BrandLink {
	link: BlokLink;

	link_text: string;
	image: BlokAsset;
}

interface CategoryItem {
	key: string | number;
	url: string;
	name: string;
	title: string;
	alt: string | undefined;
	ariaLabel: string;
	imageSrc: string | undefined;
	fallbackImageSrc: string | undefined;
}

// Limit visible sub-categories on mobile to max.7, on desktop to 12.
const visibleCategoriesCount = {
	mobile: 7,
	desktop: 12,
};

const {t} = useI18n();
const {hyphenate: addHyphens} = useHyphenation();
const route = useRoute();

const searchQuery = computed(() => route.query.query?.toString());

const {data: ctaContent} = useStoryblokStory("global/category-cta");

const categoryId = computed(() => properties.story.uuid);

const isCtaVisible = computed(
	() =>
		ctaContent.value?.content.on_pages?.length &&
		ctaContent.value?.content.on_pages.includes(categoryId.value),
);

const isModalOpen = ref(false);

const isCategoryCollapseOpen = ref(false);

const isCampaignOnly = computed(() => properties.story.content.campaign_only);

const flowboxTags = computed(() =>
	properties.story.content.flowbox_tags
		? properties.story.content.flowbox_tags.split(" ").map((tag) => tag.trim())
		: undefined,
);

const showAll = ref(false);

const scrollToTop = () => {
	window.scrollTo({top: 0, behavior: "smooth"});
};

// This is a category lander story, so it's either a simple story or an index story under a folder, with further sub-categories as its peers (either simple stories or further folders).
// The SB "links" API allows us to get the stories that are directly under this story's parent folder (if this is an index story under a folder). ALSO, it's the only SB route that returns the relative positions of each story within a given parent folder correctly - fetching a bunch of stories with `starts_with` instead would give us random positions since some peer stories could be in the same folder as the index story, others could be in sub-folders, and they have their own positioning context within those sub-folders - we need to get the positions of the stories and folders directly inside our current parent folder instead.
const {
	data: {value: subCategoryLinksData},
} = await useStoryblokLinks(
	`subcategory-links:${properties.story.parent_id}`,
	{
		// The fallback is just to satisfy TS, we won't try fetching if not relevant
		with_parent: properties.story.parent_id ?? 0,
		per_page: 50,
	},
	{pause: !(properties.story.is_startpage && properties.story.parent_id)},
);
const subCategoryLinks = (subCategoryLinksData ?? []).filter(
	(x) => x.id !== properties.story.id,
);

// Folder-type links don't have a dangling slash at the end of their slugs, but to use that slug to fetch the index *story* of that folder, it needs to have a slash.
const getLinkSlug = (link: StoryblokLink) => {
	if (link.is_folder) {
		return `${link.slug}/`;
	}
	return link.slug;
};

const categorySlugs = subCategoryLinks
	.map((x) => getLinkSlug(x))
	.join(",")
	.trim();

const {
	data: {value: subCategoriesData},
} = await useStoryblokStoriesList(
	`subcategories-${properties.story.full_slug}`,
	{
		// Slugs allow us to get simple stories and folder index stories in one go
		by_slugs: categorySlugs,
		per_page: 50,
	},
	{pause: categorySlugs.length === 0},
);

const subCategories = (subCategoriesData || [])
	// Sort the fetched sub-category stories according to the order they have in the parent folder (the links hold more accurate info for this than the stories themselves).
	.sort((a, b) => {
		const aPos =
			subCategoryLinks.find((l) => getLinkSlug(l) === a.full_slug)?.position ??
			0;
		const bPos =
			subCategoryLinks.find((l) => getLinkSlug(l) === b.full_slug)?.position ??
			0;
		return aPos - bPos;
	});

const categoryItems: CategoryItem[] = [
	...subCategories.map((c) => {
		let imageSrc;
		let fallbackImageSrc;
		if (
			c.content.menu_image?.filename &&
			c.content.menu_image?.filename.length > 0
		) {
			imageSrc = c.content.menu_image.filename;
		} else if (
			c.content.image?.filename &&
			c.content.image?.filename.length > 0
		) {
			// Fallback image if menu image is not defined.
			fallbackImageSrc = c.content.image.filename;
		}

		return {
			key: c.id,
			url: `/${c.full_slug}`,
			name: c.name,
			title: addHyphens(c.name),
			alt: c.content?.meta?.title,
			ariaLabel: c.name,
			imageSrc,
			fallbackImageSrc,
		};
	}),
	...(properties.story.content.brand_links || []).map((l, index) => {
		const imageSrc =
			l.image?.filename && l.image?.filename.length > 0
				? l.image.filename
				: undefined;
		return {
			key: l.link.url || l.link.cached_url || `brand-${index}`,
			url: formatSbUrl(l.link),
			name: l.link_text,
			title: addHyphens(l.link_text),
			alt: undefined,
			ariaLabel: l.link_text,
			imageSrc,
			fallbackImageSrc: undefined,
		};
	}),
];

const hasHiddenCategoriesInMobile = computed(
	() => categoryItems.length > visibleCategoriesCount.mobile,
);
const hasHiddenCategoriesInDesktop = computed(
	() => categoryItems.length > visibleCategoriesCount.desktop,
);

const hasActiveCampaigns = computed(
	() => properties.story.content.active_campaigns?.length > 0,
);

useSeoMeta({
	title:
		properties.story.content.meta?.title ||
		properties.story.content.title ||
		properties.story.name,

	description:
		(properties.story.content.meta?.description ||
			properties.story.content?.short_description) ??
		undefined,
	ogTitle:
		properties.story.content.meta?.title ||
		properties.story.content.title ||
		properties.story.name,

	ogDescription:
		(properties.story.content.meta?.description ||
			properties.story.content?.short_description) ??
		undefined,
	ogImage: properties.story.content?.image?.filename ?? undefined,
});

useDefaultDynamicHead({includeQueryInCanonicalUrl: false});

// CollectionPage structured data
const {appUrl} = useRuntimeConfig().public;

const {productCount} = useElevateProductListState({
	categoryId: properties.story.content.products.source?.id,
});

useJsonld(() => ({
	"@context": "https://schema.org",
	"@type": "CollectionPage",
	name:
		properties.story.content.meta?.title ||
		properties.story.content.title ||
		properties.story.name,

	description:
		(properties.story.content.meta?.description ||
			properties.story.content?.short_description) ??
		undefined,
	url: new URL(properties.story.full_slug, appUrl).href,
	mainEntity: {
		"@type": "ItemList",
		name:
			properties.story.content.meta?.title ||
			properties.story.content.title ||
			properties.story.name,
		// The product count might not be loaded during SSR, do not show it there is not reasonable value
		numberOfItems:
			productCount.value && productCount.value > 0
				? productCount.value
				: undefined,
	},
}));
</script>

<i18n lang="json">
{
	"et": {
		"amount-products": "toodet",
		"filter-title": "Kõik filtrid",
		"read-more": "Loe rohkem",
		"show-less": "Näita vähem",
		"show-all-categories": "Kuva kõik kategooriad",
		"show-less-categories": "Näita vähem kategooriaid"
	},
	"fi": {
		"amount-products": "tuotetta",
		"filter-title": "Kaikki suodattimet",
		"read-more": "Lue lisää",
		"show-less": "Näytä vähemmän",
		"show-all-categories": "Näytä kaikki kategoriat",
		"show-less-categories": "Näytä vähemmän kategorioita"
	}
}
</i18n>
